import { EmployerHealthInsuranceReductionsTable } from "./table/EmployerHealthInsuranceReductionsTable";
import { EmployerHealthInsuranceReduction } from "./types";
import { EditEmployerHealthInsuranceReduction } from "./edit/EditEmployerHealthInsuranceReduction";
import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  deleteEmployerHealthInsuranceReduction,
  fetchEmployerHealthInsuranceReductions,
  selectAllEmployerHealthInsuranceReductions,
} from "./employerHealthInsuranceReductionsSlice";
import useIsLoading from "../../../hooks/useIsLoading";
import { ModalState } from "../SalariesPage";
import { useTranslation } from "react-i18next";
import { SearchFilter } from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import { useTablePagination } from "../../../hooks/useTablePagination";
import { EMPLOYER_HEALTH_INSURANCE_REDUCTIONS_PAGE_SIZE } from "../../../common/components/functional/pagination/constants";
import { TablePaginationProps } from "../../../common/components/functional/pagination/types";
import { StyledTableContainer } from "../styled";
import { useConfirmationDialog } from "../../../contexts/ConfirmationDialogContext";
import AddButton from "../../../common/components/functional/addButton/AddButton";

type Props = {
  filters: SearchFilter[];
};

export const EmployerHealthInsuranceReductions = ({ filters }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { confirm } = useConfirmationDialog();
  const isLoading = useIsLoading(["employerHealthInsuranceReductions"]);
  const reductions: EmployerHealthInsuranceReduction[] = useAppSelector(
    (state) => selectAllEmployerHealthInsuranceReductions(state),
  );
  const { hits, cursor } = useAppSelector(
    (state) => state.employerHealthInsuranceReductions,
  );

  const [modalState, setModalState] = useState<ModalState>({
    open: false,
  });

  const deleteItem = async (id: string) => {
    const item = reductions.find((item) => item.id === id);
    if (!item) return;
    const confirmed = await confirm({
      title: t("payslips:deleteEmployerHealthInsuranceReduction"),
      contentText: t(
        "payslips:deleteEmployerHealthInsuranceReductionConfirmation",
      ),
      confirmText: t("common:delete"),
    });
    if (confirmed) {
      dispatch(
        deleteEmployerHealthInsuranceReduction({
          id: item.id,
          version: item.version,
        }),
      );
    }
  };

  const closeModal = () => {
    setModalState({ open: false, id: undefined });
  };

  const editItem = (id: string) => {
    setModalState({ open: true, id });
  };

  const dispatchSearch = useCallback(
    (cursor?: string | null) => {
      const filterObject = filters.reduce((acc, filter) => {
        const { name, value } = filter;
        if (name === "type" || !value.toString()) return acc;
        return { ...acc, [name]: value };
      }, {});
      dispatch(
        fetchEmployerHealthInsuranceReductions({
          ...filterObject,
          ...(cursor && { cursor }),
        }),
      );
    },
    [filters, dispatch],
  );

  const { pageData, count, onPageChange, pageIndex, resetPagination } =
    useTablePagination<EmployerHealthInsuranceReduction>({
      data: reductions,
      cursor,
      hits,
      pageSize: parseInt(EMPLOYER_HEALTH_INSURANCE_REDUCTIONS_PAGE_SIZE, 10),
      fetchNext: (cursor) => dispatchSearch(cursor),
      preventDispatch: true,
    });

  const paginationProps: TablePaginationProps = {
    count,
    onPageChange,
    pageIndex,
    pageSize: parseInt(EMPLOYER_HEALTH_INSURANCE_REDUCTIONS_PAGE_SIZE, 10),
  };

  useEffect(() => {
    resetPagination();
    dispatchSearch();
  }, [dispatchSearch, resetPagination, dispatch]);

  return (
    <>
      <StyledTableContainer>
        <AddButton
          label={t("common:add")}
          onClick={() => {
            setModalState({ open: true });
          }}
          variant="contained"
          sx={{ marginBottom: 1, width: "130px" }}
        />
        <EmployerHealthInsuranceReductionsTable
          isLoading={isLoading}
          items={pageData}
          editItem={editItem}
          deleteItem={deleteItem}
          pagination={paginationProps}
        />
      </StyledTableContainer>
      <EditEmployerHealthInsuranceReduction
        modalState={modalState}
        closeModal={closeModal}
      />
    </>
  );
};
